import { useShallow } from 'zustand/react/shallow'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { ROUTES } from '@/constants/routes'
import { getErrorMessage } from '@/types/firebase'
import { SignInHelpModal } from '@/components/landing/SignInHelpModal'
import {
  generatePassword,
  getPasswordChecks,
  getPasswordLabel,
  getPasswordScore,
} from '@davids-beacon/safety-core'
import { resolvePostLoginPath } from '@/lib/postLoginPath'
import { goToResolvedPath } from '@/lib/lumenHost'

const GoogleLogo = () => (
  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
)

interface Props {
  onClose: () => void
  onSwitchToLogin: () => void
}

export function RegisterModal({ onClose, onSwitchToLogin }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signInWithGoogle, signInWithFacebook, signUpWithEmail } = useAuthStore(useShallow((s) => ({ signInWithGoogle: s.signInWithGoogle, signInWithFacebook: s.signInWithFacebook, signUpWithEmail: s.signUpWithEmail })))

  const [signingIn, setSigningIn]         = useState<'google' | 'facebook' | null>(null)
  const [error, setError]                 = useState<string | null>(null)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [emailField, setEmailField]       = useState('')
  const [passwordField, setPasswordField] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [confirmTouched, setConfirmTouched]   = useState(false)
  const [showPassword, setShowPassword]   = useState(false)
  const [emailLoading, setEmailLoading]   = useState(false)
  const [emailError, setEmailError]       = useState<string | null>(null)
  const [showHelp, setShowHelp]           = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const revealTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const redirect = searchParams.get('redirect') ?? ''
  const isInvite = redirect.startsWith('/invite/') || redirect.startsWith('/org-invite/')

  const pwChecks = getPasswordChecks(passwordField)
  const pwScore  = getPasswordScore(pwChecks)
  const pwLabel  = getPasswordLabel(pwScore)

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  useEffect(() => () => { if (revealTimer.current) clearTimeout(revealTimer.current) }, [])

  async function handleSignIn(provider: 'google' | 'facebook') {
    setSigningIn(provider)
    setError(null)
    try {
      if (provider === 'facebook') await signInWithFacebook()
      else await signInWithGoogle()
      const roles = useAuthStore.getState().roles()
      goToResolvedPath(resolvePostLoginPath(roles, redirect || null), navigate)
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code
      if (code === 'auth/account-exists-with-different-credential') {
        setError('This email is linked to a different sign-in method. Try Google instead.')
      } else {
        setError(getErrorMessage(err, 'Sign up failed'))
      }
    } finally {
      setSigningIn(null)
    }
  }

  function handleGenerate() {
    const pw = generatePassword()
    setPasswordField(pw)
    setConfirmPassword(pw)
    setConfirmTouched(true)
    setShowPassword(true)
    if (revealTimer.current) clearTimeout(revealTimer.current)
    revealTimer.current = setTimeout(() => setShowPassword(false), 3000)
  }

  async function handleEmailSubmit() {
    setEmailError(null)
    if (!emailField.trim() || !passwordField) return
    if (pwScore < 4) { setEmailError('Please use a stronger password'); return }
    if (passwordField !== confirmPassword) { setEmailError('Passwords do not match'); return }
    setEmailLoading(true)
    try {
      await signUpWithEmail(emailField.trim(), passwordField)
      navigate(ROUTES.VERIFY_EMAIL)
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code
      if (code === 'auth/email-already-in-use') {
        setEmailError('An account with this email already exists.')
      } else if (code === 'auth/too-many-requests') {
        setEmailError('Too many attempts. Try again later.')
      } else {
        setEmailError(getErrorMessage(err, 'Could not create account'))
      }
    } finally {
      setEmailLoading(false)
    }
  }

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
  const emailFieldError = emailField && !isValidEmail(emailField) ? 'Enter a valid email address' : null
  const emailFormValid  = isValidEmail(emailField) && pwScore === 4 && passwordField === confirmPassword && agreedToTerms

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        {/* Modal card */}
        <div
          className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header band */}
          <div className="px-6 pt-6 pb-5 border-b border-slate-100">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <img src="/favicon.svg" alt="" className="w-8 h-8" />
              <div>
                <p className="font-extrabold text-sm text-slate-900 leading-tight">David's Beacon</p>
                <p className="text-[11px] text-slate-400">Safety tools for families</p>
              </div>
            </div>
            {isInvite ? (
              <div className="flex items-start gap-2.5 bg-beacon-50 border border-beacon-200 rounded-xl px-3 py-2.5">
                <i className="fa-solid fa-envelope-open-text text-beacon-500 mt-0.5 flex-shrink-0 text-sm" />
                <div>
                  <p className="text-xs font-bold text-beacon-800">You've been invited!</p>
                  <p className="text-xs text-beacon-700 mt-0.5">Create an account to accept.</p>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="font-bold text-lg text-slate-900 leading-tight">Create your account</h2>
                <p className="text-sm text-slate-500 mt-0.5">Free for families, always.</p>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-3">
            {!showEmailForm ? (
              <>
                {error && (
                  <div className="flex items-center gap-2 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
                    <i className="fa-solid fa-circle-exclamation text-danger-500 text-sm flex-shrink-0" />
                    <p className="text-xs text-danger-700">{error}</p>
                  </div>
                )}
                <div className="space-y-2.5">
                  <button
                    onClick={() => handleSignIn('google')}
                    disabled={!!signingIn}
                    className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-semibold rounded-2xl px-4 py-3 shadow-sm hover:shadow-md active:scale-[0.98] transition-all text-sm disabled:opacity-60"
                  >
                    {signingIn === 'google'
                      ? <i className="fa-solid fa-circle-notch animate-spin text-beacon-400 text-base" />
                      : <GoogleLogo />}
                    {signingIn === 'google' ? 'Signing up…' : 'Continue with Google'}
                  </button>
                  <button
                    onClick={() => handleSignIn('facebook')}
                    disabled={!!signingIn}
                    className="w-full flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold rounded-2xl px-4 py-3 shadow-sm hover:shadow-md active:scale-[0.98] transition-all text-sm disabled:opacity-60"
                  >
                    {signingIn === 'facebook'
                      ? <i className="fa-solid fa-circle-notch animate-spin text-lg" />
                      : <i className="fa-brands fa-facebook text-lg" />}
                    {signingIn === 'facebook' ? 'Signing up…' : 'Continue with Facebook'}
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <button
                  onClick={() => setShowEmailForm(true)}
                  className="w-full flex items-center justify-center gap-2 border-2 border-slate-200 hover:border-beacon-300 hover:bg-beacon-50 text-slate-700 hover:text-beacon-700 font-semibold rounded-2xl px-4 py-3 transition-all text-sm"
                >
                  <i className="fa-solid fa-envelope text-slate-400" />
                  Sign up with email
                </button>

                <p className="text-[11px] text-slate-400 text-center leading-snug">
                  By continuing you agree to our{' '}
                  <a href={ROUTES.TERMS} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">Terms</a>
                  {' '}and{' '}
                  <a href={ROUTES.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">Privacy Policy</a>
                </p>
              </>
            ) : (
              <div className="space-y-2.5">
                <input
                  type="email"
                  placeholder="Email address"
                  value={emailField}
                  onChange={(e) => setEmailField(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                  className={`w-full border-2 rounded-xl px-4 py-2.5 text-sm focus:outline-none transition-colors ${emailFieldError ? 'border-danger-400 focus:border-danger-400' : 'border-slate-200 focus:border-beacon-400'}`}
                />
                {emailFieldError && <p className="text-xs text-danger-500">{emailFieldError}</p>}

                <div className={`flex items-center border-2 rounded-xl bg-white transition-colors focus-within:border-beacon-400 ${passwordField && pwScore < 4 ? 'border-warning-400' : 'border-slate-200'}`}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={passwordField}
                    onChange={(e) => setPasswordField(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                    className="flex-1 px-4 py-2.5 text-sm bg-transparent outline-none"
                  />
                  <button type="button" onClick={handleGenerate} title="Generate strong password" className="px-3 text-beacon-500 hover:text-beacon-700 transition-colors">
                    <i className="fa-solid fa-wand-magic-sparkles text-sm" />
                  </button>
                  <button type="button" onClick={() => setShowPassword(v => !v)} className="pr-3 text-slate-400 hover:text-slate-600 transition-colors">
                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`} />
                  </button>
                </div>

                {passwordField.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        {[0, 1, 2, 3].map(i => (
                          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${i < pwScore ? 'bg-success-500' : 'bg-slate-200'}`} />
                        ))}
                      </div>
                      <span className={`text-xs font-semibold ${pwScore === 4 ? 'text-success-600' : 'text-slate-400'}`}>{pwLabel}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      {([
                        ['hasMinLength', '8+ characters'],
                        ['hasUppercase', '1 uppercase'],
                        ['hasNumber', '1 number'],
                        ['hasSymbol', '1 symbol'],
                      ] as const).map(([key, label]) => (
                        <div key={key} className="flex items-center gap-1.5">
                          <i className={`fa-solid ${pwChecks[key] ? 'fa-circle-check text-success-500' : 'fa-circle-xmark text-slate-300'} text-xs`} />
                          <span className={`text-xs ${pwChecks[key] ? 'text-success-700' : 'text-slate-400'}`}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className={`flex items-center border-2 rounded-xl bg-white transition-colors focus-within:border-beacon-400 ${confirmTouched && confirmPassword !== passwordField ? 'border-danger-400' : 'border-slate-200'}`}>
                  <input
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setConfirmTouched(true) }}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                    className="flex-1 px-4 py-2.5 text-sm bg-transparent outline-none"
                  />
                  {confirmTouched && confirmPassword === passwordField && passwordField.length > 0 && (
                    <i className="fa-solid fa-circle-check text-success-500 text-sm pr-3" />
                  )}
                </div>
                {confirmTouched && confirmPassword !== passwordField && (
                  <p className="text-xs text-danger-500">Passwords do not match</p>
                )}

                {emailError && (
                  <div className="flex items-center gap-2 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2">
                    <i className="fa-solid fa-circle-exclamation text-danger-500 text-xs flex-shrink-0" />
                    <p className="text-xs text-danger-700">{emailError}</p>
                  </div>
                )}

                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded accent-beacon-600 flex-shrink-0 cursor-pointer"
                  />
                  <span className="text-xs text-slate-500 leading-snug">
                    I agree to the{' '}
                    <a href={ROUTES.TERMS} target="_blank" rel="noopener noreferrer" className="underline text-slate-700 hover:text-beacon-600">Terms of Service</a>
                    {' '}and{' '}
                    <a href={ROUTES.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="underline text-slate-700 hover:text-beacon-600">Privacy Policy</a>
                  </span>
                </label>

                <button
                  onClick={handleEmailSubmit}
                  disabled={emailLoading || !emailFormValid}
                  className="w-full bg-beacon-600 hover:bg-beacon-700 disabled:opacity-50 text-white font-semibold rounded-2xl px-4 py-3 transition-colors text-sm"
                >
                  {emailLoading ? 'Creating account…' : 'Create account'}
                </button>

                <button
                  onClick={() => setShowEmailForm(false)}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors py-1"
                >
                  ← Other options
                </button>
              </div>
            )}
          </div>

          {/* Footer band */}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <button
              onClick={onSwitchToLogin}
              className="text-sm font-semibold text-beacon-600 hover:text-beacon-700 transition-colors"
            >
              Already have an account? →
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              Need help?
            </button>
          </div>
        </div>
      </div>

      {showHelp && <SignInHelpModal onClose={() => setShowHelp(false)} />}
    </>
  )
}
